import {useProjectId} from "../ProjectName";
import React, {useContext, useRef} from "react";
import {ManagerLockContext} from "../ProjectActions";
import {hasPermission, useUser} from "../../../../misc/Permission";
import {
    CentsCol,
    Column,
    FreeCell,
    LongStringCol,
    lookupNestedProp,
    StringCol,
    Table,
} from "../../../../misc/scroller/Table";
import {
    byFixedOrder,
    isNullOrUndefined,
    MarkupEditCell,
    parseMarkup,
    priceTableName
} from "./Pricing";
import {api} from "../../../../api/API";
import {MiscPrice} from "../../../../api/MiscPricing";
import {formatCents, sum} from "nate-react-api-helpers";
import {PriceLine2} from "../../../../api/Pricing";
import {removeRowOption, RowOptionCol} from "../../../logistics/purchasing/RowOptions";
import {PreviousValuePopup} from "../openings/PreviousValuePopup";
import {cellInActive} from "./cellconst";
import {MiscPricingAutoInsert} from "../../quote/pricing/MiscPricingAutoInsert";
import {getAll} from "../../../logistics/routes/useGetAll";
import {profit, sovCellCustomize, sovColumns, usePricingKit2} from "./usePricingKit2";
import {useCheckboxExtSetupData} from "../../manage/sov/CheckboxExtProvider";
import {RunningTotalContext} from "../changeset/RunningTotalContext";
import {ChangePreviewContext} from "../ShopDrawingChangePreview";

export function MiscPricing(props: {
    sov?: number;
}) {
    const project = useProjectId();
    const {locked} = useContext(ManagerLockContext)
    const u = useUser();

    const otherPricesRef = useRef<(PriceLine2[])>([])
    const miscPricesRef = useRef<MiscPrice[]>([])

    const kit = usePricingKit2(project);
    const tableName = priceTableName(project, "misc")
    const checkboxAllData = useCheckboxExtSetupData();
    const totalCtx = useContext(RunningTotalContext);

    const previewContext = useContext(ChangePreviewContext);
    const previewChangeset = previewContext.enabled ? previewContext.sessionId : undefined;

    if(!u) return null;
    if(kit.Loading) return <>{kit.Loading}</>

    return (
        <Table<MiscPrice>
            key="misc-price"
            name={tableName}
            globalPrefsName={priceTableName(0, "misc")}
            locked={(locked && !hasPermission(u, "EditShopDrawingWhenLockedToManager")) || kit.isPreview}
            fetch={async (ctx) => {

                const rs = getAll(ctx, offset => api.miscPricing.list({
                    project: project, offset,
                    previewChangeset,
                    forSovLink: !!props.sov,
                }), {useLazy: true})
                miscPricesRef.current = await rs;

                totalCtx.capture(Promise.resolve([]), rs, Promise.resolve([]));

                const others = await getAll(ctx, offset => api.pricing.list({project, offset}))
                otherPricesRef.current = others

                return checkboxAllData(rs);
            }}
            cellCustomize={props.sov ? sovCellCustomize(props.sov) :{
                backgroundColor: (row: MiscPrice, c: Column<MiscPrice>) => {
                    if(!kit.sessionId) return null;

                    let changed = false;

                    if("isChangedSinceBackup" in c && c.isChangedSinceBackup) {
                        changed = c.isChangedSinceBackup(row)
                    } else if(c.editKey) {
                        changed = !!row.backup && lookupNestedProp(row.backup, c.editKey) !== lookupNestedProp(row, c.editKey)
                    } else {
                        changed = false;
                    }

                    if(changed) {
                        return "white"
                    }

                    return cellInActive;
                },
                onFocus: (row: MiscPrice, c: Column<MiscPrice>, anchor: any) => {
                    if(!kit.sessionId) return null;
                    if(!c.editKey) return null

                    if(!!row.backup && lookupNestedProp(row.backup, c.editKey) !== lookupNestedProp(row, c.editKey)) {
                        return (
                            <PreviousValuePopup anchor={anchor} width={c.width} value={c.render(row.backup, c)} />
                        )
                    }

                    return null;
                }
            }}
            columns={[
                !locked && RowOptionCol({
                    options: (dt) => [
                        removeRowOption(dt, {
                            refreshTables: [tableName],
                            onComplete: () => {}
                        })
                    ],
                }),
                ...sovColumns(props.sov),
                StringCol("Name", "name"),
                CentsCol("Unit Cost", "unitCostCents"),
                {
                    name: "Markup",
                    render: dt => {
                        if(!isNullOrUndefined(dt.markupPercent)) return dt.markupPercent + "%"
                        if(!isNullOrUndefined(dt.markupCents)) return "$" + formatCents(dt.markupCents)
                        return "auto"
                    },
                    editable: {
                        type: "custom",
                        copy: dt => {
                            if(!isNullOrUndefined(dt.markupPercent)) return dt.markupPercent + "%"
                            if(!isNullOrUndefined(dt.markupCents)) return "$" + formatCents(dt.markupCents)
                            return "auto"
                        },
                        paste: (rw, value) => {
                            Object.assign(rw, parseMarkup(value))
                        },
                        render: MarkupEditCell,
                    },
                    isChangedSinceBackup: (row: MiscPrice) => {
                        if(!row.backup) return false;
                        if(lookupNestedProp(row.backup, "markupPercent") !== lookupNestedProp(row, "markupPercent")) return true;
                        if(lookupNestedProp(row.backup, "markupCents") !== lookupNestedProp(row, "markupCents")) return true;
                        return false;
                    },
                    alignRight: true,
                    width: 80,
                },
                CentsCol("Total", "extendedPrice"),
                hasPermission(u, "CanViewProjectPricingProfit") && {
                    name: "Proj. Profit",
                    render: dt => {
                        return formatCents(profit(dt))
                    },
                    alignRight: true,
                    width: 90,
                },
                {
                    name: "Proj. Margin",
                    render: dt => {
                        let profit = dt.extendedPrice - dt.unitCostCents * 1;
                        if(profit === 0) return "0%"
                        let margin = Math.round(profit / dt.extendedPrice * 100);
                        return margin + "%"
                    },
                    alignRight: true,
                    width: 100,
                },
                LongStringCol("Public Note", "publicNote", 400),
                StringCol("Internal Note", "internalNote", 400),
            ]}
            groupBy={props.sov ? undefined : {
                groupFx: (input: MiscPrice) => content, // everything is 1 big group
                fixedGroups: [content, total],
                groupSorter: byFixedOrder([content, total]),
                afterGroup: (input) => {
                    const subTotal = sum(miscPricesRef.current.map(a => a.extendedPrice - (a.backup?.extendedPrice || 0)));
                    const otherPrices = otherPricesRef.current.reduce((acc, a) => acc + a.extendedPrice, 0);

                    if(input.name === total) {
                        return (
                            <>
                                <FreeCell index={0} colSpan={4}>
                                    <b>
                                        Total
                                    </b>
                                </FreeCell>
                                <FreeCell index={4}>
                                    <div style={{textAlign: "right"}}>
                                        {formatCents(otherPrices + subTotal)}
                                    </div>
                                </FreeCell>
                            </>
                        )
                    } else if(input.name === content) {
                        return (
                            <>
                                <FreeCell index={0} colSpan={4}>
                                    <b>
                                        Sub Total
                                    </b>
                                </FreeCell>
                                <FreeCell index={4}>
                                    <div style={{textAlign: "right"}}>
                                        {formatCents(subTotal)}
                                    </div>
                                </FreeCell>
                            </>
                        )
                    }
                }
            }}
            fetchDeps={[project]}
            onChange={(input) => api.miscPricing.upsert(input)}
            insert={kit.isPreview ? undefined : {
                buttonText: "New Row",
                alignX: "left",
                modal: input => <MiscPricingAutoInsert onDone={input} />,
            }}
        />
    )
}

const content = "content-group"
const total = "total-group"